import { useState } from 'react';
import { DAY, calc, dateOf, dur, fDate, fShort, inr, span, startOfDay, toInstant, type BookingRecord, type Phase } from '@nandanam/domain';
import { useStore, type AppStore } from '../app/store';
import { Icon } from '../ui/icons';
import { Avatar, Empty, PageHead, Segment, StatusPill } from '../ui/components';
import { DEP_LABEL, pkgLabel } from '../app/selectors';

/** [key, label, test, shown only when chosen from a summary tile] */
type F = [string, string, (p: Phase, r: BookingRecord) => boolean, boolean?];

export function pipelineFilters(S: AppStore, isEnq: boolean): F[] {
  const now = S.now, t0 = startOfDay(dateOf(now));
  const recent = (r: BookingRecord) => now - toInstant(r.created) < 90 * DAY;
  return isEnq ? [
    ['open', 'Open', p => p === 'enquiry' || p === 'hold'],
    ['new', 'New', (p, r) => p === 'enquiry' && now - toInstant(r.created) < 2 * DAY],
    ['follow', 'Follow-up due', (p, r) => p === 'enquiry' && !!r.followUp && startOfDay(r.followUp) <= t0],
    ['hold', 'On hold', p => p === 'hold'],
    ['expired', 'Expired', p => p === 'expired'],
    ['converted', 'Converted', p => ['upcoming', 'inprogress', 'closing', 'completed'].includes(p)],
    ['lost', 'Lost / cancelled', p => p === 'lost' || p === 'cancelled'],
    ['all', 'All', () => true],
    ['recent', 'Last 90 days', (_p, r) => recent(r), true],
    ['reached', 'Reached hold · 90 days', (_p, r) => recent(r) && (!!r.hold || r.holdHistory.length > 0 || ['confirmed', 'completed', 'cancelled'].includes(r.status)), true],
    ['booked90', 'Converted · 90 days', (_p, r) => recent(r) && ['confirmed', 'completed', 'cancelled'].includes(r.status), true],
  ] : [
    ['upcoming', 'Upcoming', p => p === 'upcoming'],
    ['active', 'On site / closing', p => p === 'inprogress' || p === 'closing'],
    ['completed', 'Completed', p => p === 'completed'],
    ['cancelled', 'Cancelled', p => p === 'cancelled'],
    ['all', 'All bookings', p => ['upcoming', 'inprogress', 'closing', 'completed', 'cancelled'].includes(p)],
    ['current', 'Active bookings', p => ['upcoming', 'inprogress', 'closing'].includes(p), true],
    ['balance', 'Balance due', (p, r) => ['upcoming', 'inprogress', 'closing'].includes(p) && calc(r).balance > 0, true],
  ];
}

export function matchRecord(S: AppStore, r: BookingRecord, q: string) {
  q = q.toLowerCase().trim();
  if (!q) return true;
  const c = S.cust(r.cust), qd = q.replace(/\D/g, '');
  return c.name.toLowerCase().includes(q) || S.num(r).toLowerCase().includes(q) || (qd.length >= 3 && [c.phone, c.whatsapp, c.alt].join(' ').replace(/\D/g, '').includes(qd));
}

export function usePipeline(isEnq: boolean, filter: string | undefined, q: string, source: string) {
  const S = useStore(), now = S.now;
  const F = pipelineFilters(S, isEnq);
  const fKey = filter && F.some(f => f[0] === filter) ? filter : F[0]![0];
  const fn = F.find(f => f[0] === fKey)![2];
  const base = S.state.records.filter(r => (!isEnq || !source || r.source === source) && matchRecord(S, r, q));
  const filters = F.filter(f => !f[3] || f[0] === fKey).map(f => ({ v: f[0], label: f[1], count: base.filter(r => f[2](S.phase(r), r)).length }));
  const list = base.filter(r => fn(S.phase(r), r));
  const st = (r: BookingRecord) => toInstant(span(r).start);
  list.sort(isEnq
    ? (a, b) => { const ha = S.phase(a) === 'hold', hb = S.phase(b) === 'hold'; if (ha !== hb) return ha ? -1 : 1; const fa = a.followUp ? startOfDay(a.followUp) : st(a), fb = b.followUp ? startOfDay(b.followUp) : st(b); return fa - fb; }
    : (a, b) => (fKey === 'completed' || fKey === 'cancelled' ? st(b) - st(a) : st(a) - st(b)));
  const win = S.state.records.filter(r => now - toInstant(r.created) < 90 * DAY);
  const reachedHold = win.filter(r => r.hold || r.holdHistory.length || ['confirmed', 'completed', 'cancelled'].includes(r.status)).length;
  const conf = win.filter(r => ['confirmed', 'completed', 'cancelled'].includes(r.status)).length;
  const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) + '%' : '');
  const openNow = S.state.records.filter(r => ['enquiry', 'hold'].includes(S.phase(r))).length;
  const holding = S.state.records.filter(r => S.phase(r) === 'hold').length;
  const act = S.state.records.filter(r => ['upcoming', 'inprogress', 'closing'].includes(S.phase(r)));
  const outstanding = act.reduce((t, r) => t + Math.max(0, calc(r).balance), 0), bookedVal = act.reduce((t, r) => t + calc(r).total, 0);
  const kpis = isEnq ? [
    { label: 'Enquiries · 90 days', v: String(win.length), rate: '', note: 'All sources', icon: 'inbox', tint: '#eef1f4', fg: '#4e5d6b', filter: 'recent' },
    { label: 'Reached hold', v: String(reachedHold), rate: pct(reachedHold, win.length), note: 'of enquiries', icon: 'clock', tint: '#f5f5dc', fg: '#6b6230', filter: 'reached' },
    { label: 'Converted', v: String(conf), rate: pct(conf, win.length), note: 'Enquiry → booking', icon: 'check', tint: '#e6efe9', fg: '#46705c', filter: 'booked90' },
    { label: 'Open now', v: String(openNow), rate: '', note: `${holding} holding dates`, icon: 'phone', tint: '#dff1f9', fg: '#26708f', filter: 'open' },
  ] : [
    { label: 'Upcoming bookings', v: String(act.filter(r => S.phase(r) === 'upcoming').length), rate: '', note: 'Confirmed, not yet arrived', icon: 'calendar', tint: '#e1ebe9', fg: '#2F4F4F', filter: 'upcoming' },
    { label: 'Booked value', v: inr(bookedVal), rate: '', note: 'Active bookings', icon: 'chart', tint: '#dff1f9', fg: '#26708f', filter: 'current' },
    { label: 'Outstanding', v: inr(outstanding), rate: '', note: `${act.filter(r => calc(r).balance > 0).length} with a balance`, icon: 'rupee', tint: '#fdf0e6', fg: '#a5552c', filter: 'balance' },
    { label: 'Completed', v: String(S.state.records.filter(r => r.status === 'completed').length), rate: '', note: 'All time', icon: 'check', tint: '#e6efe9', fg: '#46705c', filter: 'completed' },
  ];
  return { filters, fKey, list, kpis };
}

export const SRC_ICON: Record<string, string> = { Phone: 'phone', WhatsApp: 'message', Instagram: 'insta' };
const AV: [string, string][] = [['#e1ebe9', '#2F4F4F'], ['#dff1f9', '#26708f'], ['#f5f5dc', '#6b6230'], ['#eef1f4', '#4e5d6b'], ['#fdf0e6', '#a5552c']];
export const avatarColors = (name: string) => AV[[...name].reduce((t, ch) => t + ch.charCodeAt(0), 0) % AV.length]!;
export const DEP_TONE: Record<string, [string, string]> = { due: ['#fdf0e6', '#a5552c'], held: ['#e1ebe9', '#2F4F4F'], inspection: ['#f5f5dc', '#6b6230'], refunded: ['#e6efe9', '#46705c'], partial: ['#dff1f9', '#26708f'], forfeited: ['#f6e8e3', '#a85a4a'] };

export function followText(S: AppStore, r: BookingRecord) {
  if (S.phase(r) !== 'enquiry' || !r.followUp) return null;
  const t0 = startOfDay(dateOf(S.now)), fu = startOfDay(r.followUp);
  return { text: fu <= t0 ? `${fu < t0 ? 'Overdue' : 'Today'} · ${fDate(r.followUp, { dow: false })}` : fDate(r.followUp), late: fu <= t0 };
}

export function Pipeline() {
  const S = useStore();
  const isEnq = S.route.name === 'enquiries';
  const [q, setQ] = useState('');
  const [source, setSource] = useState('');
  const { filters, fKey, list, kpis } = usePipeline(isEnq, S.route.params.filter, q, source);
  const setFilter = (f: string) => S.nav(S.route.name, { filter: f });
  return (
    <div className="page">
      <PageHead icon={isEnq ? 'inbox' : 'book'} title={isEnq ? 'Enquiries' : 'Bookings'} sub={isEnq ? 'Every lead from phone, WhatsApp and Instagram' : 'Confirmed stays and events'}>
        <label className="search"><Icon name="search" size={14} /><input placeholder="Search name, phone or ID" value={q} onChange={e => setQ(e.target.value)} /></label>
        <button className="btn primary" onClick={() => S.open('new', {})}><Icon name="plus" size={15} />New enquiry</button>
      </PageHead>
      <div className="kpis">
        {kpis.map(k => (
          <button key={k.label} type="button" className={`kpi kpi-link ${fKey === k.filter ? 'on' : ''}`} onClick={() => { setFilter(k.filter); setTimeout(() => document.getElementById('pipeline-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); }}>
            <div className="lbl"><span className="tile" style={{ background: k.tint, color: k.fg }}><Icon name={k.icon} size={18} /></span>{k.label}</div>
            <div className="val">{k.v} {k.rate && <span style={{ fontSize: 13, color: 'var(--good-text)', fontWeight: 600 }}>{k.rate}</span>}</div>
            <div className="note">{k.note}</div>
          </button>
        ))}
      </div>
      <div id="pipeline-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between', scrollMarginTop: 80 }}>
        <Segment value={fKey} options={filters} onChange={setFilter} />
        {isEnq && (
          <div className="chips">
            {[['', 'All'] as const, ...S.state.settings.sources.map(s => [s, s] as const)].map(([v, l]) => (
              <button key={v} className={`chip ${source === v ? 'on' : ''}`} onClick={() => setSource(v)}>{v && <Icon name={SRC_ICON[v] ?? 'message'} size={13} />}{l}</button>
            ))}
          </div>
        )}
      </div>
      <div className="card" style={{ padding: 8 }}>
        {list.length === 0 ? <Empty /> : (
          <div className="table-wrap">
            <table className="t cards">
              <thead><tr><th>Customer</th><th>Requirement</th><th>Dates</th>{isEnq ? <><th>Quote</th><th>Status</th><th className="r">Actions</th></> : <><th>Payment</th><th>Status</th></>}</tr></thead>
              <tbody>{list.map(r => <PipelineRow key={r.id} r={r} isEnq={isEnq} />)}</tbody>
            </table>
          </div>
        )}
      </div>
      <div className="muted" style={{ fontSize: 12.5 }}>{isEnq ? 'Enquiries never block dates. Holds block them until they expire.' : 'Click a booking to see payments, deposit, documents and messages.'}</div>
    </div>
  );
}

function PipelineRow({ r, isEnq }: { r: BookingRecord; isEnq: boolean }) {
  const S = useStore(), ph = S.phase(r), c = S.cust(r.cust), k = calc(r), sp = span(r), isStay = r.kind === 'stay';
  const [abg, afg] = avatarColors(c.name);
  const open = () => S.nav('booking', { id: r.id });
  const fu = followText(S, r);
  return (
    <tr className="click" onClick={open}>
      <td>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Avatar name={c.name} bg={abg} fg={afg} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{c.name}</div>
            <div className="muted" style={{ fontSize: 12, display: 'flex', gap: 5, alignItems: 'center' }}>
              {isEnq ? <><Icon name={SRC_ICON[r.source] ?? 'message'} size={12} />{r.source}</> : <span className="num">{S.num(r)}</span>}
              {S.isPendingRecord(r) && <span className="pill st-neutral" style={{ fontSize: 10 }}>Not in OneDrive yet</span>}
            </div>
          </div>
        </div>
      </td>
      <td className="m-full">
        <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
          <span className="tile" style={{ width: 28, height: 28, borderRadius: 9, background: isStay ? '#e1ebe9' : '#dff1f9', color: isStay ? '#2F4F4F' : '#26708f' }}><Icon name={isStay ? 'bed' : 'cake'} size={14} /></span>
          <div><div style={{ fontWeight: 600 }}>{r.kind === 'combo' ? `${r.type} + stay` : r.type}</div><div className="muted" style={{ fontSize: 12 }}>{pkgLabel(r)} · {r.guests} guests</div></div>
        </div>
      </td>
      <td className="num" style={{ whiteSpace: 'nowrap' }}>{fShort(sp.start, sp.end)}</td>
      {isEnq ? (
        <>
          <td className="num" data-l="Quote">{k.pending ? 'To be agreed' : inr(k.total)}</td>
          <td>
            <StatusPill phase={ph} />
            {ph === 'hold' && r.hold && <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>expires in {dur(toInstant(r.hold.expires) - S.now)}</div>}
            {ph === 'lost' && r.lost && <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>{r.lost.reason}</div>}
            {fu && <div style={{ fontSize: 11.5, marginTop: 4, color: fu.late ? 'var(--warn)' : 'var(--muted)' }}>Follow up {fu.text}</div>}
          </td>
          <td className="r m-full" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'inline-flex', gap: 6 }}>
              <button className="icon-btn wa" title="WhatsApp" onClick={() => S.open('message', { id: r.id })}><Icon name="message" size={15} /></button>
              {ph === 'enquiry' && (k.pending
                ? <button className="btn small primary" disabled={!S.canEdit} onClick={() => S.open('pricing', { id: r.id })}>Set price</button>
                : <button className="btn small primary" disabled={!S.canEdit} onClick={() => S.open('hold', { id: r.id })}>Place hold</button>)}
              {ph === 'hold' && <button className="btn small primary" disabled={!S.canEdit} onClick={() => S.open('confirm', { id: r.id })}>Confirm</button>}
              {ph === 'expired' && <button className="btn small primary" disabled={!S.canEdit} onClick={() => S.open('hold', { id: r.id })}>Hold again</button>}
            </div>
          </td>
        </>
      ) : (
        <>
          <td className="m-end" style={{ minWidth: 160 }}>
            <div className="num" style={{ fontWeight: 600 }}>{inr(k.total)}</div>
            <div className="num" style={{ fontSize: 12, color: ph === 'cancelled' ? 'var(--muted)' : k.balance > 0 ? 'var(--warn)' : 'var(--good-text)' }}>{ph === 'cancelled' ? `Retained ${inr(r.cancel?.retained ?? 0)}` : k.balance > 0 ? `${inr(k.balance)} due` : k.balance < 0 ? `${inr(-k.balance)} overpaid` : 'Paid in full'}</div>
            <div className="progress"><span style={{ width: `${k.total ? Math.min(100, Math.round((k.paid / k.total) * 100)) : 0}%`, background: k.paid >= k.total ? '#5b8a72' : '#5aaed6' }} /></div>
          </td>
          <td>
            <StatusPill phase={ph} />
            {ph !== 'cancelled' && <div style={{ marginTop: 5 }}><span className="pill" style={{ background: DEP_TONE[k.dStatus]![0], color: DEP_TONE[k.dStatus]![1], fontSize: 11 }}><Icon name="shield" size={11} />Deposit {DEP_LABEL[k.dStatus]!.toLowerCase()}{k.dHeld ? ` · ${inr(k.dHeld)}` : ''}</span></div>}
          </td>
        </>
      )}
    </tr>
  );
}
